/* eslint-disable no-unreachable */
import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  useGetAllCriticalNationalities,
  useGetAllNationalities,
} from "../../../hooks/common/query";
import { useTranslation } from "react-i18next";
import SelectInput from "../../../components/inputs/SelectInput";
import { SelectInputOption } from "../../../constants/common";
import { useForm } from "react-hook-form";
import { Button } from "../../../components/button";
import { useAddUpdateCriticalNationalities } from "../../../hooks/common/mutation";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import Loader from "../../../components/loader";

const CriticalNationalitiesConfig = () => {
  const { t, i18n } = useTranslation();

  const { handleSubmit } = useForm();

  const { data: allNationalities } = useGetAllNationalities();

  const {
    mutateAsync: AddUpdateCriticalNationalities,
    isPending: isAddUpdateCriticalNationalitiesLoading,
  } = useAddUpdateCriticalNationalities();

  const {
    data: criticalNationalities,
    isPending: isCriticalNationalitiesLoading,
  } = useGetAllCriticalNationalities();

  useEffect(() => {
    if (criticalNationalities) {
      const mappedCriticalNationalities = criticalNationalities?.data?.map(
        (item: {
          nationality: string;
          nationalityEn: string;
          nationalityAr: string;
        }) => ({
          value: item.nationality,
          label:
            i18n.language === "en" ? item.nationalityEn : item.nationalityAr,
          color: "#403f68",
        })
      );
      setSelectedCriticalNationalitiesOptions(mappedCriticalNationalities);
    }
  }, [criticalNationalities, i18n.language]);

  const [
    selectedCriticalNationalitiesOptions,
    setSelectedCriticalNationalitiesOptions,
  ] = useState<SelectInputOption[]>([]);

  const nationalities = useMemo(
    () =>
      allNationalities?.map(
        (item: { iso3: string; name: string; name_Ar: string }) => ({
          value: item.iso3,
          label: i18n.language === "en" ? item.name : item.name_Ar,
          color: "#403f68",
        })
      ),
    [allNationalities, i18n.language]
  );

  const onSubmit = useCallback(async () => {
    try {
      const body = {
        nationalities: selectedCriticalNationalitiesOptions?.map((item) => ({
          nationality: item.value,
        })),
      };

      await AddUpdateCriticalNationalities(body);
      toast.success(t("criticalNationalitiesUpdatedSuccessfully"));
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  }, [AddUpdateCriticalNationalities, selectedCriticalNationalitiesOptions, t]);

  const onClear = useCallback(() => {
    setSelectedCriticalNationalitiesOptions([]);
  }, []);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("criticalNationalitiesConfig")}
          </h1>
        </BackButtonWrapper>

        {isCriticalNationalitiesLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4 md:gap-8"
          >
            <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
              <SelectInput
                label={t("criticalNationalities")}
                options={nationalities}
                selectedOptions={selectedCriticalNationalitiesOptions}
                setSelectedOptions={setSelectedCriticalNationalitiesOptions}
              />

              <div className="flex items-center gap-2">
                <Button isLoading={isAddUpdateCriticalNationalitiesLoading}>
                  {t("save")}
                </Button>
                <Button
                  type="button"
                  onClick={onClear}
                  className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
                >
                  {t("clear")}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default CriticalNationalitiesConfig;
